import { z } from "zod"
import { parseThreshold } from "./threshold"

const dqdlRuleSchema = z
  .object({
    type: z.enum([
      "IsComplete",
      "IsPrimaryKey",
      "ColumnValues",
      "CustomSql",
      "DataFreshness",
      "ColumnLength",
      "ColumnDataType",
      "Completeness",
      "DistinctValuesCount",
    ]),
    column: z.string().nullish(),
    columns: z.array(z.string()).nullish(),
    pattern: z.string().nullish(),
    threshold: z.string().nullish(),
    sql: z.string().nullish(),
    days: z.number().nullish(),
    min: z.number().nullish(),
    max: z.number().nullish(),
    dataType: z.string().nullish(),
    where: z.string().nullish(),
  })
  .superRefine((rule, ctx) => {
    switch (rule.type) {
      case "IsComplete":
      case "DataFreshness":
      case "ColumnLength":
      case "Completeness":
      case "DistinctValuesCount":
        if (!rule.column?.trim()) {
          ctx.addIssue({ code: "custom", message: "Column is required", path: ["column"] })
        }
        break
      case "IsPrimaryKey":
        if (!rule.columns?.length) {
          ctx.addIssue({
            code: "custom",
            message: "At least one column is required",
            path: ["columns"],
          })
        }
        break
      case "ColumnValues":
        if (!rule.column?.trim()) {
          ctx.addIssue({ code: "custom", message: "Column is required", path: ["column"] })
        }
        if (!rule.pattern?.trim()) {
          ctx.addIssue({ code: "custom", message: "Pattern is required", path: ["pattern"] })
        }
        break
      case "CustomSql":
        if (!rule.sql?.trim()) {
          ctx.addIssue({ code: "custom", message: "SQL query is required", path: ["sql"] })
        }
        break
    }

    if (rule.type === "DataFreshness" && (rule.days == null || rule.days < 1)) {
      ctx.addIssue({ code: "custom", message: "Days must be at least 1", path: ["days"] })
    }
    if (rule.type === "Completeness" && rule.threshold != null && rule.threshold !== "") {
      const parsed = parseThreshold(rule.threshold)
      if (!parsed.ok) {
        const message =
          parsed.reason === "range"
            ? "Threshold must be between 0% and 100%"
            : "Threshold must be a number"
        ctx.addIssue({ code: "custom", message, path: ["threshold"] })
      }
    }
  })

const fileRuleSchema = z.object({
  name: z.string(),
  nameRegex: z.string(),
  maxLength: z.number().min(1, "Max length must be at least 1").nullish(),
  allowedExtensions: z.string(),
  required: z.boolean(),
  count: z.number().nullish(),
  validateStudyId: z.boolean(),
  noTimestampWhenZipped: z.boolean().nullish(),
})

const piiRuleSchema = z.object({
  enabled: z.boolean(),
  columns: z.array(z.string()),
})

export const dataQualityFormSchema = z.object({
  fileMatching: z.object({
    pattern: z.string(),
  }),
  preConformance: z.object({
    uploadFormat: z.object({
      allowZip: z.boolean(),
      allowUnzipped: z.boolean(),
      requireZip: z.boolean(),
    }),
    zipFile: z.object({
      nameRegex: z.string().catch(""),
      maxLength: z.number().min(1, "Max length must be at least 1").nullish(),
      validateStudyId: z.boolean().nullish(),
    }),
    fileCount: z.object({
      min: z.number().min(0).nullish(),
      max: z.number().min(0).nullish(),
      exact: z.number().nullish(),
    }),
    files: z.array(fileRuleSchema),
  }),
  majorRules: z.array(dqdlRuleSchema),
  majorPii: piiRuleSchema,
  minorRules: z.array(dqdlRuleSchema),
  minorPii: piiRuleSchema,
  table: z.object({
    name: z
      .string()
      .min(1, "A table name is required")
      .max(255, "Must be no more than 255 characters")
      .refine(
        (val) => /^[a-z0-9_]+$/.test(val),
        "Use only lowercase letters, numbers, and underscores",
      )
      .refine((val) => /^[a-z0-9].*[a-z0-9]$/.test(val) || /^[a-z0-9]$/.test(val), {
        message: "Must begin and end with a letter or number",
      })
      .refine((val) => !val.startsWith("aws"), {
        message: "Must not begin with the reserved prefix 'aws'",
      })
      .refine((val) => !val.includes("__"), {
        message: "Must not contain consecutive underscores",
      }),
    primaryKeyFields: z.array(z.string()),
    scd2: z.object({ enabled: z.boolean() }).optional(),
  }),
})
